import { removeAbbr } from '@/utils/time'
import { buildBarChartOptions, buildDataPointsByDay, fillDate } from '@/components/patient-stats/common/utils'
import * as R from 'ramda'
import moment from 'moment-timezone'
import { CHART_DATE_FORMAT } from '@/components/patient-stats/constants'
import { durationTimestampToTimeLabel, withSingularOrPluralLabel } from '@/utils/labels'
import { DataPoint } from '@/jsonapi/CommonTypes'
import { buildAxis, buildMarkLine } from '@/components/patient-stats/graphs/detailed-reports/options/option-builders'
import { ONE_MINUTE_TIMESTAMP } from '@/components/patient-stats/graphs/mapping-chart-options/constants'
import { round } from '@/components/exercises/measure/filters'
import YAxis = echarts.EChartOption.YAxis
import XAxis = echarts.EChartOption.XAxis

const METERS_PER_STRIDE = 0.75 // for now
const ACTIVE_METERS_PER_DAY_THRESHOLD = 3000 // if this person is sedentary (care home etc, show a smaller range)

const ACTIVE_WARNING_BELOW_STEPS = 8000
const INACTIVE_WARNING_BELOW_STEPS = 500 // if this person is sedentary (care home etc, show a smaller range)

export function buildWalkDistanceOptions(options: {
  resultStartTimestamp: number
  resultEndTimestamp: number
  timezone: string
  colors: Record<any, any> // vuetify theme
  casualWalkSteps: DataPoint[]
}): echarts.EChartOption<echarts.EChartOption.SeriesLine> {
  const { timezone, resultStartTimestamp, resultEndTimestamp, colors, casualWalkSteps } = options

  const xAxisLabels: string[] = [...fillDate(timezone, resultStartTimestamp, resultEndTimestamp)].map(timestamp =>
    String(timestamp)
  )

  const eqByTimestamps = R.curry(R.eqProps)('timestamp')
  // @ts-ignore
  // const uniqStepCounts = R.uniqWith(eqByTimestamps)(casualWalkSteps) as DataPoint[]

  // const stepCountsByDayMap: { [key: string]: number } = buildDataPointsByDay(uniqStepCounts, timezone)
  // const seriesXData: Array<[string, number]> = Object.entries(stepCountsByDayMap)
  const walkDistances = casualWalkSteps.map(function(day) {
    return { timestamp: day.timestamp, value: day.value * METERS_PER_STRIDE } as DataPoint
  })
  console.log('distances: ')
  console.log(walkDistances)

  const uniqDistances = R.uniqWith(eqByTimestamps)(walkDistances) as DataPoint[]
  const distancesByDayMap: { [key: string]: number } = buildDataPointsByDay(uniqDistances, timezone)

  const walkDistancesByDay: { [key: string]: number } = buildDataPointsByDay(walkDistances, timezone)

  const maxYValue = Math.max(10000, Math.max(...Object.values(walkDistancesByDay)))
  const seriesData: Array<[string, number]> = Object.entries(walkDistancesByDay).sort(
    (a, b) => Number(a[0]) - Number(b[0])
  )

  const isActive = maxYValue > ACTIVE_METERS_PER_DAY_THRESHOLD
  const lines = isActive ? [2000, 5000, 10000, 15000] : [500, 1000, 1500, 2000]

  return {
    grid: [{ x: '5%', y: '5%', width: '90%', height: '90%', containLabel: true } as echarts.EChartOption.Grid],
    baseColor: colors.teal,
    lowValueColor: colors.burgundy,
    threshold: 3000,
    xAxis: buildAxis({
      data: xAxisLabels,
      axisType: 'x',
      formatter: param => {
        return moment(parseInt(param, 10))
          .tz(timezone)
          .format(CHART_DATE_FORMAT.DATE)
      },
    }) as XAxis,
    yAxis: buildAxis({
      axisType: 'y',
      maxValue: maxYValue,
    }) as YAxis,
    visualMap: [
      {
        show: false,
        type: 'continuous',
        seriesIndex: 1,
        min: 0,
        max: isActive ? ACTIVE_WARNING_BELOW_STEPS : INACTIVE_WARNING_BELOW_STEPS,
        inRange: {
          color: [colors.burgundy, colors.teal],
        },
      },
    ],
    series: [
      {
        type: 'line',
        silent: true,
        stack: 'inhaler usage',
        // @ts-ignore
        markLine: buildMarkLine(lines),
      },
      {
        type: 'line',
        silent: true,
        label: { show: false },
        // @ts-ignore
        data: seriesData.map(d => ({
          value: [d[0], d[1]],
        })),
      },
    ],
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'shadow',
      },
      formatter: params => {
        const date = moment(parseInt(params[0].axisValue, 10))
          .tz(timezone)
          .format(removeAbbr(timezone, CHART_DATE_FORMAT.DAY_ABBR))

        let distance = params[0].value[1]
        if (distance > 1000) {
          distance = (distance / 1000).toFixed(1) + ' km'
        } else {
          distance = distance + ' meters'
        }

        return `${date}<br>est. ${distance}`
      },
    },
  }
}
